import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, updateDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { K, inputStyle } from '../constants';
import { SecHd, Btn, AddBtn, Lbl } from '../components/ui';
import { useUserProfile } from '../hooks/useUserProfile';
import { Lock, Mic, Search, X, Star, Pencil, ChevronUp, ChevronDown, ArrowRight } from 'lucide-react';

/* ── constants ── */
const MODES = [
  { id: 'fire',     label: 'Fire',     col: K.red  },
  { id: 'ems',      label: 'EMS',      col: K.ems  },
  { id: 'command',  label: 'Command',  col: K.info },
  { id: 'wildland', label: 'Wildland', col: K.warn },
  { id: 'general',  label: 'General',  col: K.teal },
];

const DEFAULT_CATEGORIES = ['Guidelines', 'SOPs', 'Manuals', 'Reference'];

const TYPE_OPTS = [
  { value: 'youtube', label: '📺 YouTube Video' },
  { value: 'pdf',     label: '📄 PDF Document'  },
  { value: 'image',   label: '🖼️ Image'          },
  { value: 'link',    label: '🔗 Web Link'       },
];

const TYPE_ICON = { youtube: '📺', pdf: '📄', image: '🖼️', link: '🔗' };
const TYPE_COL  = { youtube: K.bad, pdf: K.info, image: K.ok, link: K.gold };

function getYouTubeId(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
}

function modeCol(modeId) {
  return (MODES.find((m) => m.id === modeId) || MODES[0]).col;
}

/* Merge default categories with custom Firestore categories for a mode.
   Returns array of category name strings in order: defaults first, then custom. */
function getCatNames(firestoreCategories, modeId) {
  const custom = firestoreCategories
    .filter((c) => c.mode === modeId)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((c) => c.name);
  const all = [...DEFAULT_CATEGORIES];
  for (const name of custom) {
    if (!all.includes(name)) all.push(name);
  }
  return all;
}

/* ── Login gate ── */
function LoginGate({ onShowAuth }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}><Lock size={48} color={K.t2} strokeWidth={1.75} /></div>
      <div style={{ fontFamily: K.fSans, fontSize: 28, fontWeight: 700, color: K.t0, marginBottom: 8 }}>Members Only</div>
      <div style={{ fontSize: 13, color: K.t2, marginBottom: 28, lineHeight: 1.6 }}>
        Training materials and resources are available to Central Mason Fire & EMS members. Log in to access.
      </div>
      <Btn onClick={onShowAuth} col={K.red} block>Log In to Access</Btn>
    </div>
  );
}

/* ── PDF Viewer Modal ── */
function PdfViewer({ url, title, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#000000dd' }} />
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 501, display: 'flex', flexDirection: 'column', padding: '8px 8px env(safe-area-inset-bottom, 8px)', maxWidth: 600, margin: '0 auto' }}>
        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: K.bg1, borderRadius: '10px 10px 0 0', borderBottom: `1px solid ${K.bdr}` }}>
          <div style={{ fontFamily: K.fSans, fontSize: 16, fontWeight: 600, color: K.t0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 12 }}>{title}</div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '4px 10px', borderRadius: 8, fontSize: 11, textDecoration: 'none', fontFamily: K.fSans }}>
              New Tab
            </a>
            <button className="hc-btn" onClick={onClose} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={16} color={K.t1} strokeWidth={1.75} /></button>
          </div>
        </div>
        {/* PDF iframe */}
        <iframe
          src={url}
          title={title}
          style={{ flex: 1, width: '100%', border: 'none', background: '#fff', borderRadius: '0 0 10px 10px' }}
        />
      </div>
    </>
  );
}

/* ── PDF thumbnail renderer ── */
function PdfThumbnail({ url, height = 100 }) {
  const canvasRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        const ctx = canvas.getContext('2d');
        const vp = page.getViewport({ scale: 1 });
        const scale = (height * 2) / vp.height; // 2x for retina
        const scaled = page.getViewport({ scale });
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        if (!cancelled) setLoaded(true);
      } catch { /* silently fail — fallback icon shown */ }
    })();
    return () => { cancelled = true; };
  }, [url, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none',
      }}
    />
  );
}

/* ── Resource card ── */
function ResourceCard({ res, isAdmin, onDelete, onEdit, onViewPdf, isFavorite, onToggleFavorite }) {
  const [playing, setPlaying] = useState(false);
  const ytId  = res.type === 'youtube' ? getYouTubeId(res.url) : null;
  const icon  = TYPE_ICON[res.type] || '📎';
  const col   = TYPE_COL[res.type]  || K.t1;

  const handleOpen = () => {
    if (res.type === 'pdf') { onViewPdf(res); }
    else if (res.type === 'youtube' && ytId) { setPlaying(true); }
    else { window.open(res.url, '_blank'); }
  };

  // Expanded YouTube player
  if (res.type === 'youtube' && ytId && playing) {
    return (
      <div style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 10, overflow: 'hidden', marginBottom: 0 }}>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
          <iframe
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={res.title}
          />
        </div>
        <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: K.fSans, fontSize: 14, fontWeight: 600, color: K.t0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.title}</div>
          <button className="hc-btn" onClick={() => setPlaying(false)} style={{ background: 'none', border: 'none', color: K.t3, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={14} color={K.t3} strokeWidth={1.75} /></button>
        </div>
      </div>
    );
  }

  // Thumbnail preview
  const thumb = res.type === 'image' ? res.url
    : res.type === 'youtube' && ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`
    : null;

  return (
    <button className="hc-btn" onClick={handleOpen} style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Preview area */}
      <div style={{ width: '100%', height: 100, background: `linear-gradient(135deg, ${K.bg2}, ${K.bg3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {thumb ? (
          <img src={thumb} alt={res.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : res.type === 'pdf' ? (
          <>
            <PdfThumbnail url={res.url} height={100} />
            <span style={{ position: 'absolute', fontSize: 28, pointerEvents: 'none' }}>{icon}</span>
          </>
        ) : (
          <span style={{ fontSize: 28 }}>{icon}</span>
        )}
        {/* Type badge */}
        <div style={{ position: 'absolute', top: 6, left: 6, background: `${col}dd`, color: '#fff', fontSize: 9, fontWeight: 600, fontFamily: K.fSans, letterSpacing: 0.3, padding: '2px 6px', borderRadius: 4 }}>
          {res.type === 'youtube' ? '▶ Video' : res.type === 'pdf' ? 'PDF' : res.type === 'image' ? 'Image' : 'Link'}
        </div>
      </div>
      {/* Title area */}
      <div style={{ padding: '8px 10px', width: '100%', borderTop: `1px solid ${K.bdr}`, borderLeft: `3px solid ${col}` }}>
        <div style={{ fontFamily: K.fSans, fontSize: 13, fontWeight: 600, color: K.t0, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.title}</div>
        {res.description ? <div style={{ fontSize: 10, color: K.t3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.description}</div> : null}
      </div>
      {/* Top-right controls: favorite + admin */}
      <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 3, zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
        {onToggleFavorite && (
          <button
            className="hc-btn"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(res.id); }}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            title={isFavorite ? 'Unfavorite' : 'Favorite'}
            style={{ background: isFavorite ? `${K.gold}dd` : '#000a', backdropFilter: 'blur(4px)', border: 'none', color: isFavorite ? K.bg : '#fff', padding: '3px 6px', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', lineHeight: 1 }}
          >
            <Star size={14} color={isFavorite ? K.bg : '#fff'} fill={isFavorite ? K.bg : 'none'} strokeWidth={1.75} />
          </button>
        )}
        {isAdmin && (
          <>
            <button className="hc-btn" onClick={(e) => { e.stopPropagation(); onEdit(res); }} style={{ background: '#000a', backdropFilter: 'blur(4px)', border: 'none', color: '#fff', padding: '3px 6px', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={13} color="#fff" strokeWidth={1.75} /></button>
            <button className="hc-btn" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${res.title}"?`)) onDelete(res.id); }} style={{ background: '#000a', backdropFilter: 'blur(4px)', border: 'none', color: '#fff', padding: '3px 6px', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={13} color="#fff" strokeWidth={1.75} /></button>
          </>
        )}
      </div>
    </button>
  );
}

/* ── Manage Categories Modal ── */
function ManageCategoriesModal({ categories, mode, accent, onClose }) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const modeCats = categories
    .filter((c) => c.mode === mode)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    await addDoc(collection(db, 'resourceCategories'), {
      name, mode, order: Date.now(), createdAt: serverTimestamp(),
    });
    setNewName('');
    setSaving(false);
  };

  const renameCategory = async (id) => {
    const name = editName.trim();
    if (!name) return;
    await updateDoc(doc(db, 'resourceCategories', id), { name });
    setEditingId(null);
    setEditName('');
  };

  const removeCategory = async (id, name) => {
    if (window.confirm(`Delete category "${name}"? Resources in this category won't be deleted.`)) {
      await deleteDoc(doc(db, 'resourceCategories', id));
    }
  };

  const moveCategory = async (id, direction) => {
    const idx = modeCats.findIndex((c) => c.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= modeCats.length) return;
    const a = modeCats[idx];
    const b = modeCats[swapIdx];
    await updateDoc(doc(db, 'resourceCategories', a.id), { order: b.order });
    await updateDoc(doc(db, 'resourceCategories', b.id), { order: a.order });
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />
      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />
        <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 600, color: K.t0, marginBottom: 6 }}>
          Manage Categories
        </div>
        <div style={{ fontSize: 12, color: K.t2, marginBottom: 20 }}>
          {MODES.find((m) => m.id === mode)?.label} mode
        </div>

        {/* Add new */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCategory()}
            placeholder="New category name" style={{ ...inputStyle(), flex: 1 }} />
          <Btn col={accent} onClick={addCategory} disabled={saving || !newName.trim()}>Add</Btn>
        </div>

        {/* List */}
        {modeCats.length === 0 ? (
          <div style={{ textAlign: 'center', color: K.t3, padding: 20, fontSize: 13 }}>
            No categories yet. Add one above.
          </div>
        ) : (
          modeCats.map((cat, i) => (
            <div key={cat.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 0',
              borderBottom: i < modeCats.length - 1 ? `1px solid ${K.bdr}` : 'none',
            }}>
              {/* Reorder arrows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button className="hc-btn" onClick={() => moveCategory(cat.id, -1)} disabled={i === 0}
                  style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center' }}><ChevronUp size={14} color={i === 0 ? K.bg3 : K.t2} strokeWidth={1.75} /></button>
                <button className="hc-btn" onClick={() => moveCategory(cat.id, 1)} disabled={i === modeCats.length - 1}
                  style={{ background: 'none', border: 'none', cursor: i === modeCats.length - 1 ? 'default' : 'pointer', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center' }}><ChevronDown size={14} color={i === modeCats.length - 1 ? K.bg3 : K.t2} strokeWidth={1.75} /></button>
              </div>

              {/* Name / Edit */}
              {editingId === cat.id ? (
                <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && renameCategory(cat.id)}
                    style={{ ...inputStyle(), flex: 1, padding: '8px 10px' }} autoFocus />
                  <button className="hc-btn" onClick={() => renameCategory(cat.id)}
                    style={{ background: accent, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, fontWeight: 600 }}>Save</button>
                  <button className="hc-btn" onClick={() => setEditingId(null)}
                    style={{ background: K.bg3, color: K.t2, border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 11, fontFamily: K.fSans }}>Cancel</button>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, fontSize: 14, color: K.t0, fontWeight: 600 }}>{cat.name}</div>
                  <button className="hc-btn" onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}
                    style={{ background: 'none', border: `1px solid ${K.bdr}`, padding: '4px 8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={13} color={K.t3} strokeWidth={1.75} /></button>
                  <button className="hc-btn" onClick={() => removeCategory(cat.id, cat.name)}
                    style={{ background: 'none', border: `1px solid ${K.bdr}`, padding: '4px 8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={13} color={K.t3} strokeWidth={1.75} /></button>
                </>
              )}
            </div>
          ))
        )}

        <button className="hc-btn" onClick={onClose} style={{ width: '100%', marginTop: 20, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px' }}>
          Close
        </button>
      </div>
    </>
  );
}

/* ── Edit resource modal ── */
function EditResourceModal({ res, categories, onClose }) {
  const [title, setTitle]           = useState(res.title || '');
  const [description, setDescription] = useState(res.description || '');
  const [resMode, setResMode]       = useState(res.mode || 'general');
  const [category, setCategory]     = useState(res.category || '');
  const [newCategory, setNewCategory] = useState('');
  const [useNewCat, setUseNewCat]   = useState(false);
  const [error, setError]           = useState('');
  const [saving, setSaving]         = useState(false);

  const editAccent = modeCol(resMode);

  const catNames = getCatNames(categories, resMode);

  const finalCategory = useNewCat ? newCategory.trim() : category;

  // When mode changes, reset category to first available or new
  const handleModeChange = (newMode) => {
    setResMode(newMode);
    const newCats = getCatNames(categories, newMode);
    setCategory(newCats[0] || '');
    setUseNewCat(false);
  };

  const save = async () => {
    if (!title.trim())  { setError('Title is required.'); return; }
    if (!finalCategory) { setError('Category is required.'); return; }
    setError('');
    setSaving(true);
    try {
      if (useNewCat && newCategory.trim()) {
        await addDoc(collection(db, 'resourceCategories'), {
          name: newCategory.trim(), mode: resMode, order: Date.now(), createdAt: serverTimestamp(),
        });
      }
      await updateDoc(doc(db, 'resources', res.id), {
        title: title.trim(),
        description: description.trim(),
        category: finalCategory,
        mode: resMode,
      });
      onClose();
    } catch {
      setError('Save failed. Try again.');
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />
      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />
        <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 600, color: K.t0, marginBottom: 20 }}>Edit Resource</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <Lbl t="Title" />
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle()} />
          </div>
          <div>
            <Lbl t="Description (optional)" />
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" style={inputStyle()} />
          </div>
          <div>
            <Lbl t="Mode" />
            <div className="hc-tab-scroll">
              {MODES.map((m) => (
                <button key={m.id} className="hc-btn" onClick={() => handleModeChange(m.id)} style={{
                  flex: '0 0 auto',
                  background: resMode === m.id ? m.col : K.bg2,
                  color: resMode === m.id ? '#fff' : K.t2,
                  border: `1px solid ${resMode === m.id ? m.col : K.bdr}`,
                  borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                  fontFamily: K.fSans,
                  fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
                  whiteSpace: 'nowrap',
                }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Lbl t="Category" />
            {!useNewCat && catNames.length > 0 ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer', flex: 1 }}>
                  {catNames.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="hc-btn" onClick={() => setUseNewCat(true)} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>+ New</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Category name" style={{ ...inputStyle(), flex: 1 }} />
                {catNames.length > 0 && (
                  <button className="hc-btn" onClick={() => setUseNewCat(false)} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>Existing</button>
                )}
              </div>
            )}
          </div>
        </div>

        {error ? <div style={{ fontSize: 11, color: K.bad, marginTop: 12 }}>{error}</div> : null}

        <div style={{ marginTop: 20 }}>
          <Btn onClick={save} col={editAccent} block>{saving ? 'Saving...' : 'Save Changes'}</Btn>
        </div>
        <button className="hc-btn" onClick={onClose} style={{ width: '100%', marginTop: 8, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px' }}>
          Cancel
        </button>
      </div>
    </>
  );
}

/* ── Add resource modal ── */
function AddResourceModal({ user, categories, mode, accent, onClose }) {
  const [title, setTitle]           = useState('');
  const [description, setDescription] = useState('');
  const [type, setType]             = useState('youtube');
  const [url, setUrl]               = useState('');
  const [file, setFile]             = useState(null);
  const catNames = getCatNames(categories, mode);
  const [category, setCategory]     = useState(catNames[0] || '');
  const [newCategory, setNewCategory] = useState('');
  const [useNewCat, setUseNewCat]   = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState('');

  const finalCategory = useNewCat ? newCategory.trim() : category;

  const handleTypeChange = (t) => { setType(t); setUrl(''); setFile(null); };

  const save = async () => {
    if (!title.trim())    { setError('Title is required.'); return; }
    if (!finalCategory)   { setError('Category is required.'); return; }
    setError('');

    let finalUrl = url.trim();

    if (!finalUrl && file) {
      setUploading(true);
      try {
        const sRef = storageRef(storage, `resources/${Date.now()}_${file.name}`);
        const task = uploadBytesResumable(sRef, file);
        finalUrl = await new Promise((resolve, reject) => {
          task.on('state_changed', null, reject, async () => {
            resolve(await getDownloadURL(task.snapshot.ref));
          });
        });
      } catch {
        setError('Upload failed. Try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!finalUrl) { setError('A URL or file is required.'); return; }

    // If using a new category, add it to resourceCategories
    if (useNewCat && newCategory.trim()) {
      await addDoc(collection(db, 'resourceCategories'), {
        name: newCategory.trim(), mode, order: Date.now(), createdAt: serverTimestamp(),
      });
    }

    await addDoc(collection(db, 'resources'), {
      title:       title.trim(),
      description: description.trim(),
      type,
      url:         finalUrl,
      category:    finalCategory,
      mode,
      order:       Date.now(),
      createdAt:   serverTimestamp(),
      createdBy:   user.uid,
    });
    onClose();
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />
      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />
        <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 600, color: K.t0, marginBottom: 20 }}>Add Resource</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <Lbl t="Title" />
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Resource title" style={inputStyle()} />
          </div>
          <div>
            <Lbl t="Description (optional)" />
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" style={inputStyle()} />
          </div>
          <div>
            <Lbl t="Type" />
            <select value={type} onChange={(e) => handleTypeChange(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer' }}>
              {TYPE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Lbl t={type === 'youtube' ? 'YouTube URL' : type === 'link' ? 'URL' : 'URL (or upload file below)'} />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={type === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://'}
              style={inputStyle()}
            />
          </div>
          {(type === 'pdf' || type === 'image') && (
            <div>
              <Lbl t="Or Upload File" />
              <input
                type="file"
                accept={type === 'pdf' ? 'application/pdf' : 'image/*'}
                onChange={(e) => setFile(e.target.files[0] || null)}
                style={{ ...inputStyle(), padding: '10px 14px', cursor: 'pointer' }}
              />
            </div>
          )}
          <div>
            <Lbl t="Category" />
            {!useNewCat && catNames.length > 0 ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer', flex: 1 }}>
                  {catNames.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="hc-btn" onClick={() => setUseNewCat(true)} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>+ New</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="e.g. SOGs, Training Videos, Maps" style={{ ...inputStyle(), flex: 1 }} />
                {catNames.length > 0 && (
                  <button className="hc-btn" onClick={() => setUseNewCat(false)} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>Existing</button>
                )}
              </div>
            )}
          </div>
        </div>

        {error ? <div style={{ fontSize: 11, color: K.bad, marginTop: 12 }}>{error}</div> : null}

        <div style={{ marginTop: 20 }}>
          <Btn onClick={save} col={accent} block>{uploading ? 'Uploading...' : 'Save Resource'}</Btn>
        </div>
        <button className="hc-btn" onClick={onClose} style={{ width: '100%', marginTop: 8, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px' }}>
          Cancel
        </button>
      </div>
    </>
  );
}

/* ── Main screen ── */
export function Resources({ user, isAdmin, onShowAuth, initialMode, goTab, showPodcastLink }) {
  const [resources, setResources]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [mode, setMode]             = useState(initialMode || 'fire');
  const [search, setSearch]         = useState('');
  const [showAdd, setShowAdd]       = useState(false);
  const [editing, setEditing]       = useState(null);
  const [showManageCats, setShowManageCats] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);
  const { profile, updateProfile } = useUserProfile(user);

  const favoriteIds = profile?.favoriteResourceIds || [];
  const isFavorite = (id) => favoriteIds.includes(id);
  const toggleFavorite = async (id) => {
    if (!user) return;
    const next = isFavorite(id) ? favoriteIds.filter((x) => x !== id) : [...favoriteIds, id];
    try {
      await updateProfile({ favoriteResourceIds: next });
    } catch (err) {
      console.error('[Resources] favorite toggle failed:', err);
    }
  };

  const searching = search.trim().length > 0;
  const accent = modeCol(mode);

  // Subscribe to resources
  useEffect(() => {
    const q = query(collection(db, 'resources'), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      setResources(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Subscribe to categories
  useEffect(() => {
    const q = query(collection(db, 'resourceCategories'), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Filter resources for current mode (treat legacy resources without mode as 'general')
  const modeResources = resources.filter((r) => (r.mode || 'general') === mode);

  // Merged list: defaults + custom
  const allCatNames = getCatNames(categories, mode);

  // Group resources by category, maintaining category order
  const grouped = useMemo(() => {
    const map = {};
    for (const r of modeResources) {
      const cat = r.category || 'Uncategorized';
      if (!map[cat]) map[cat] = [];
      map[cat].push(r);
    }
    const ordered = [];
    for (const name of allCatNames) {
      if (map[name]) {
        ordered.push({ name, items: map[name] });
        delete map[name];
      }
    }
    for (const name of Object.keys(map)) {
      ordered.push({ name, items: map[name] });
    }
    return ordered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, categories, mode]);

  // Cross-mode search results
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return resources.filter((r) => {
      const t = (r.title || '').toLowerCase();
      const d = (r.description || '').toLowerCase();
      const c = (r.category || '').toLowerCase();
      return t.includes(q) || d.includes(q) || c.includes(q);
    });
  }, [resources, search]);

  if (!user) return <LoginGate onShowAuth={onShowAuth} />;

  const handleDelete = (id) => deleteDoc(doc(db, 'resources', id));

  return (
    <div>
      <SecHd title="Resources" sub="Training materials, SOGs, and reference documents." />

      {showPodcastLink && goTab && (
        <button
          onClick={() => goTab('podcast')}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            background: K.bg1, border: `1px solid ${K.bdr}`, borderLeft: `3px solid ${K.gold}`,
            borderRadius: 12, padding: '10px 14px', cursor: 'pointer',
            marginBottom: 12, textAlign: 'left',
            fontFamily: K.fSans,
          }}
        >
          <Mic size={22} color={K.gold} strokeWidth={1.75} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: K.fSans, fontSize: 14, fontWeight: 600, color: K.gold, letterSpacing: 0.3 }}>Department Podcast</span>
            <span style={{ display: 'block', fontSize: 11, color: K.t2, marginTop: 2 }}>Watch the latest episodes</span>
          </span>
          <ArrowRight size={16} color={K.gold} strokeWidth={1.75} />
        </button>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search all resources..."
          style={{ ...inputStyle(), width: '100%', paddingLeft: 36, paddingRight: search ? 36 : 14 }}
        />
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none' }}><Search size={14} color={K.t3} strokeWidth={1.75} /></span>
        {search && (
          <button className="hc-btn" onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}><X size={14} color={K.t3} strokeWidth={1.75} /></button>
        )}
      </div>

      {/* Search Results */}
      {searching ? (
        <>
          <div style={{ fontSize: 11, color: K.t3, marginBottom: 12, fontFamily: K.fSans }}>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} across all modes
          </div>
          {searchResults.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: K.t3, fontSize: 13 }}>
              No resources match your search.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {searchResults.map((res) => {
                const resMode = MODES.find((m) => m.id === (res.mode || 'general'));
                return (
                  <div key={res.id} style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, background: resMode?.col || K.t3, color: '#fff', fontSize: 9, fontWeight: 600, fontFamily: K.fSans, letterSpacing: 0.3, padding: '2px 8px', borderRadius: 4 }}>
                      {resMode?.label.split(' ')[1] || 'General'}
                    </div>
                    <ResourceCard res={res} isAdmin={isAdmin} onDelete={handleDelete} onEdit={setEditing} onViewPdf={setViewingPdf} isFavorite={isFavorite(res.id)} onToggleFavorite={toggleFavorite} />
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Mode Switcher */}
          <div className="hc-tab-scroll" style={{ marginBottom: 16 }}>
            {MODES.map((m) => (
              <button key={m.id} className="hc-btn" onClick={() => setMode(m.id)} style={{
                flex: '0 0 auto',
                minWidth: 0,
                background: mode === m.id ? m.col : K.bg1,
                color: mode === m.id ? '#fff' : K.t2,
                border: `1px solid ${mode === m.id ? m.col : K.bdr}`,
                borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                fontFamily: K.fSans,
                fontSize: 13, fontWeight: 600, letterSpacing: 0.3,
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Admin Controls */}
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <AddBtn onClick={() => setShowAdd(true)} col={accent}>+ Add Resource</AddBtn>
              </div>
              <button className="hc-btn" onClick={() => setShowManageCats(true)} style={{
                background: K.bg1, border: `1px solid ${accent}`, color: accent,
                borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
                fontFamily: K.fSans, fontSize: 11, fontWeight: 600,
                letterSpacing: 0.3, whiteSpace: 'nowrap',
              }}>
                Categories
              </button>
            </div>
          )}

          {/* Category count pills */}
          {allCatNames.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {allCatNames.map((catName) => {
                const count = modeResources.filter((r) => r.category === catName).length;
                if (count === 0) return null;
                return (
                  <div key={catName} style={{
                    background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 20,
                    padding: '4px 12px', fontSize: 11, color: K.t2,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ fontWeight: 600, color: K.t1 }}>{catName}</span>
                    <span style={{
                      background: accent + '22', color: accent, fontSize: 10,
                      fontWeight: 700, borderRadius: 10, padding: '1px 6px',
                    }}>{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Favorites (for current mode) */}
          {(() => {
            const favorites = modeResources.filter((r) => isFavorite(r.id));
            if (favorites.length === 0) return null;
            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  fontSize: 12, color: K.gold, letterSpacing: 0.3,
                  marginBottom: 10, paddingBottom: 6,
                  borderBottom: `1px solid ${K.bdr}`, fontWeight: 600, fontFamily: K.fSans,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}><Star size={14} color={K.gold} fill={K.gold} strokeWidth={1.75} />Favorites</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {favorites.map((res) => (
                    <ResourceCard key={res.id} res={res} isAdmin={isAdmin} onDelete={handleDelete} onEdit={setEditing} onViewPdf={setViewingPdf} isFavorite={true} onToggleFavorite={toggleFavorite} />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Resources by category */}
          {modeResources.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: K.t3, fontSize: 13 }}>
              {isAdmin
                ? `No ${MODES.find((m) => m.id === mode)?.label.split(' ')[1]} resources yet. Add one above.`
                : 'No resources have been added to this section yet.'}
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.name} style={{ marginBottom: 24 }}>
                <div style={{
                  fontSize: 12, color: accent, letterSpacing: 0.3,
                  marginBottom: 10, paddingBottom: 6,
                  borderBottom: `1px solid ${K.bdr}`, fontWeight: 600, fontFamily: K.fSans,
                }}>{group.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {group.items.map((res) => (
                    <ResourceCard key={res.id} res={res} isAdmin={isAdmin} onDelete={handleDelete} onEdit={setEditing} onViewPdf={setViewingPdf} isFavorite={isFavorite(res.id)} onToggleFavorite={toggleFavorite} />
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Modals */}
      {showAdd && (
        <AddResourceModal user={user} categories={categories} mode={mode} accent={accent} onClose={() => setShowAdd(false)} />
      )}
      {editing && (
        <EditResourceModal res={editing} categories={categories} onClose={() => setEditing(null)} />
      )}
      {showManageCats && (
        <ManageCategoriesModal categories={categories} mode={mode} accent={accent} onClose={() => setShowManageCats(false)} />
      )}
      {viewingPdf && (
        <PdfViewer url={viewingPdf.url} title={viewingPdf.title} onClose={() => setViewingPdf(null)} />
      )}
    </div>
  );
}
